import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { GenProvider, useGenContext } from './contexts/GenContext';
import Sidebar from './components/Sidebar';
import QuickGen from './pages/QuickGen';
import ToolkitPage from './pages/Toolkit';
import SettingsPage from './pages/Settings';
import StyleTransferPage from './pages/StyleTransfer';
import ImageGallery from './pages/ImageGallery';
import StampManager from './pages/StampManager';
import LoginPopup from './components/LoginPopup';
import { useDarkMode } from './hooks/useDarkMode';

// 手機頂部列右側顯示的當前頁標題（與 Sidebar 選單名稱一致）
const TAB_TITLE: Record<string, string> = {
    toolkit: '里務工具包',
    'style-transfer': '人像風格生成',
    gallery: '圖片庫',
    'stamp-manager': '印章管理',
    settings: '系統設定',
};

const AppLayout: React.FC = () => {
    useDarkMode();
    const { showLoginPopup } = useAppContext();
    const { activeTab, appliedToolkit, toast, clearToast } = useGenContext();
    // 手機 sidebar drawer 開關狀態（桌面 ≥lg 無作用）
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    // toolkit 套用後實際畫面是「快速生成」
    const currentTitle = activeTab === 'toolkit' && appliedToolkit ? '快速生成' : TAB_TITLE[activeTab] ?? '';

    return (
        <div className="flex h-screen bg-gray-100 dark:bg-slate-950 text-gray-900 dark:text-slate-100 font-sans overflow-hidden">
            <LoginPopup isOpen={showLoginPopup} />

            {/* 手機 drawer 背後遮罩，點擊關閉 */}
            {mobileSidebarOpen && (
                <div
                    onClick={() => setMobileSidebarOpen(false)}
                    className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
                    aria-hidden="true"
                />
            )}

            <Sidebar
                mobileOpen={mobileSidebarOpen}
                onMobileClose={() => setMobileSidebarOpen(false)}
            />

            {activeTab !== 'ai-collab' && (
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] dark:from-slate-900 dark:via-slate-950 dark:to-black">
                    {/* 手機頂部列：hamburger + logo */}
                    <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
                        <button
                            onClick={() => setMobileSidebarOpen(true)}
                            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors focus:outline-none"
                            aria-label="開啟選單"
                        >
                            <Menu size={22} />
                        </button>
                        <div className="w-8 h-8 rounded-lg overflow-hidden shadow shadow-blue-500/20">
                            <img src="/favicon-128x128.png" alt="logo" className="w-full h-full object-cover" />
                        </div>
                        <h1 className="text-base font-bold tracking-tight text-slate-900 dark:text-white">里嗨AI</h1>
                        {/* 右側顯示當前頁標題，讓內容區的 H2 可在手機隱藏 */}
                        {currentTitle && (
                            <span className="ml-auto text-sm font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[40%]">
                                {currentTitle}
                            </span>
                        )}
                    </div>

                    <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
                        {activeTab === 'toolkit' && (appliedToolkit ? <QuickGen /> : <ToolkitPage />)}
                        {activeTab === 'style-transfer' && <StyleTransferPage />}
                        {activeTab === 'gallery' && <ImageGallery />}
                        {activeTab === 'stamp-manager' && <StampManager />}
                        {activeTab === 'settings' && <SettingsPage />}
                    </div>
                </main>
            )}

            {/* Toast 通知 */}
            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
                    <div className="flex items-center gap-3 bg-slate-900 dark:bg-slate-700 text-white px-5 py-3 rounded-2xl shadow-xl">
                        <span className="text-blue-400 text-lg">✦</span>
                        <span className="text-sm font-medium">{toast}</span>
                        <button onClick={clearToast} className="ml-2 text-slate-400 hover:text-white transition-colors text-xs">✕</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const App: React.FC = () => (
    <AppProvider>
        <GenProvider>
            <AppLayout />
        </GenProvider>
    </AppProvider>
);

export default App;
